import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { documentService } from '../../services/documentService';
import { DocumentContentDto, PublicDocumentDetailsDto } from '../../types/documents';

const readOnlyModules = { toolbar: false };

const readOnlyQuillStyles = `
.quill-readonly-content .ql-toolbar.ql-snow{display:none}
.quill-readonly-content .ql-container.ql-snow{border:0;font-size:.875rem}
.quill-readonly-content .ql-editor{padding:0;min-height:80px;white-space:pre-wrap;word-break:break-word}
`;

function fileExt(fileName?: string): string {
  if (!fileName) return '';
  const p = fileName.split('.').pop();
  return p?.toLowerCase() ?? '';
}

function isRichTextHtmlPayload(nodeType: string | undefined, content: DocumentContentDto): boolean {
  const ext = fileExt(content.fileName);
  const ct = (content.contentType || '').toLowerCase();
  const looksEdocOrHtml =
    nodeType === 'edoc' ||
    ext === 'edoc' ||
    ext === 'html' ||
    ext === 'htm' ||
    ct.includes('html');
  return looksEdocOrHtml && Boolean(content.content?.trim());
}

function MainContentPreview({ content, nodeType }: { content: DocumentContentDto; nodeType?: string }) {
  const url = content.url?.trim();
  const ext = fileExt(content.fileName);
  const ct = (content.contentType || '').toLowerCase();

  if (isRichTextHtmlPayload(nodeType, content)) {
    return (
      <>
        <style>{readOnlyQuillStyles}</style>
        <div className="quill-readonly-content">
          <ReactQuill
            key={content.id}
            value={content.content}
            readOnly
            theme="snow"
            modules={readOnlyModules}
          />
        </div>
      </>
    );
  }

  if (!url) {
    return (
      <p className="text-sm text-gray-600">
        Visualização do arquivo não disponível para este documento.
      </p>
    );
  }

  if (ext === 'pdf' || ct.includes('pdf')) {
    return (
      <iframe
        title="Documento"
        src={url}
        className="h-[min(80vh,900px)] w-full rounded-md border border-gray-200 bg-white"
      />
    );
  }

  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext) || ct.startsWith('image/')) {
    return (
      <img
        src={url}
        alt={content.fileName || 'Imagem'}
        className="max-h-[min(80vh,900px)] w-auto max-w-full rounded-md border border-gray-200 object-contain"
      />
    );
  }

  if (ext === 'html' || ext === 'htm' || ct.includes('html')) {
    return (
      <iframe
        title="Documento"
        src={url}
        sandbox="allow-same-origin allow-scripts"
        className="h-[min(80vh,900px)] w-full rounded-md border border-gray-200 bg-white"
      />
    );
  }

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
      <p className="mb-2 text-sm text-gray-700">
        Abra ou baixe o arquivo para visualizar.
      </p>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm font-medium text-blue-600 hover:text-blue-800"
      >
        {content.fileName || 'Baixar arquivo'}
      </a>
    </div>
  );
}

const PublicDocumentViewPage: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const [data, setData] = useState<PublicDocumentDetailsDto | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!documentId) {
      setData(null);
      return;
    }

    let cancelled = false;
    setError(null);
    setData(undefined);

    void documentService
      .getPublicDocumentDetails(documentId)
      .then((dto) => {
        if (!cancelled) setData(dto);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Não foi possível carregar o documento.');
          setData(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [documentId]);

  if (data === undefined && !error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="text-gray-600">Carregando…</p>
        </div>
      </div>
    );
  }

  if (error || data === null || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
          <h1 className="mb-2 text-lg font-semibold text-gray-900">Documento não disponível</h1>
          <p className="mb-6 text-sm text-gray-600">
            O conteúdo não foi encontrado ou não está disponível para acesso público.
          </p>
          <Link
            to="/login"
            className="text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            Ir para o login
          </Link>
        </div>
      </div>
    );
  }

  const coverUrl = data.cover?.url?.trim();
  const coverExt = fileExt(data.cover?.fileName);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <span className="text-sm font-medium text-gray-800">Visualização pública</span>
          <Link to="/login" className="text-sm text-blue-600 hover:text-blue-800">
            Entrar
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8">
        <h1 className="mb-2 text-2xl font-bold text-gray-900">{data.title}</h1>
        {data.description ? (
          <p className="mb-8 text-gray-600">{data.description}</p>
        ) : (
          <div className="mb-8" />
        )}

        {coverUrl && ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(coverExt) && (
          <div className="mb-8">
            <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-gray-500">Capa</h2>
            <img
              src={coverUrl}
              alt="Capa"
              className="max-h-64 rounded-md border border-gray-200 object-contain"
            />
          </div>
        )}

        {data.content && (
          <section className="mb-10">
            <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-gray-500">
              Conteúdo
            </h2>
            <MainContentPreview content={data.content} nodeType={data.nodeType} />
          </section>
        )}

        {(data.attachments ?? []).length > 0 && (
          <section>
            <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-gray-500">
              Anexos
            </h2>
            <ul className="divide-y divide-gray-200 rounded-md border border-gray-200 bg-white">
              {(data.attachments ?? []).map((a) => (
                <li key={a.id} className="flex items-center justify-between px-4 py-3 text-sm">
                  <span className="truncate text-gray-800">{a.fileName || 'Anexo'}</span>
                  {a.url ? (
                    <a
                      href={a.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-4 shrink-0 font-medium text-blue-600 hover:text-blue-800"
                    >
                      Abrir
                    </a>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
};

export default PublicDocumentViewPage;
